import { Suspense, lazy } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";

const Landing = lazy(() => import("@/pages/Landing"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const Layout = lazy(() => import("@/components/Layout"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Patients = lazy(() => import("@/pages/Patients"));
const PatientProfile = lazy(() => import("@/pages/PatientProfile"));
const Appointments = lazy(() => import("@/pages/Appointments"));
const Treatments = lazy(() => import("@/pages/Treatments"));
const Finance = lazy(() => import("@/pages/Finance"));
const Reports = lazy(() => import("@/pages/Reports"));
const Staff = lazy(() => import("@/pages/Staff"));
const Lab = lazy(() => import("@/pages/Lab"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const Support = lazy(() => import("@/pages/Support"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const Callbacks = lazy(() => import("@/pages/Callbacks"));
const RecycleBin = lazy(() => import("@/pages/RecycleBin"));
const Settings = lazy(() => import("@/pages/Settings"));
const Subscription = lazy(() => import("@/pages/Subscription"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentFail = lazy(() => import("@/pages/PaymentFail"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const WhatsAppInbox = lazy(() => import("@/pages/WhatsAppInbox"));
const DuePayments = lazy(() => import("@/pages/DuePayments"));
const Reactivation = lazy(() => import("@/pages/Reactivation"));
const TomorrowAppointments = lazy(() => import("@/pages/TomorrowAppointments"));
const AIAssistant = lazy(() => import("@/pages/AIAssistant"));
const OnlineBooking = lazy(() => import("@/pages/OnlineBooking"));
const Legal = lazy(() => import("@/pages/Legal"));
const Contact = lazy(() => import("@/pages/Contact"));
const Notes = lazy(() => import("@/pages/Notes"));
const Audit = lazy(() => import("@/pages/Audit"));

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <FullLoader />;
  if (!user) return <Navigate to="/giris" replace />;
  return children;
}

function SuperAdminOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <FullLoader />;
  if (!user) return <Navigate to="/giris" replace />;
  if (user.role !== "super_admin") return <Navigate to="/panel" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <FullLoader />;
  if (user) {
    if (user.role === "super_admin") return <Navigate to="/admin" replace />;
    return <Navigate to="/panel" replace />;
  }
  return children;
}

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-muted-foreground">Yükleniyor...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<FullLoader />}>
            <Routes>
              {/* Public */}
              <Route path="/" element={<Landing />} />
              <Route path="/iletisim" element={<Contact />} />
              <Route path="/yasal/:slug" element={<Legal />} />
              <Route path="/online-randevu/:clinicId" element={<OnlineBooking />} />
              <Route path="/odeme/basarili" element={<PaymentSuccess />} />
              <Route path="/odeme/hatali" element={<PaymentFail />} />
              <Route path="/eposta-dogrula" element={<VerifyEmail />} />

              {/* Public-only auth pages */}
              <Route path="/giris" element={<PublicOnly><Login /></PublicOnly>} />
              <Route path="/kayit" element={<PublicOnly><Register /></PublicOnly>} />
              <Route path="/sifremi-unuttum" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
              <Route path="/sifre-sifirla" element={<PublicOnly><ResetPassword /></PublicOnly>} />

              {/* Super Admin */}
              <Route path="/admin" element={<SuperAdminOnly><AdminPanel /></SuperAdminOnly>} />
              <Route path="/admin/whatsapp" element={<SuperAdminOnly><WhatsAppInbox /></SuperAdminOnly>} />

              {/* Clinic protected */}
              <Route element={<Protected><Layout><Outlet /></Layout></Protected>}>
                <Route path="/panel" element={<Dashboard />} />
                <Route path="/hastalar" element={<Patients />} />
                <Route path="/hastalar/:patientId" element={<PatientProfile />} />
                <Route path="/randevular" element={<Appointments />} />
                <Route path="/tedaviler" element={<Treatments />} />
                <Route path="/finans" element={<Finance />} />
                <Route path="/gunu-gelmis-odemeler" element={<DuePayments />} />
                <Route path="/yarinki-randevular" element={<TomorrowAppointments />} />
                <Route path="/ai-asistan" element={<AIAssistant />} />
                <Route path="/hastayi-geri-kazan" element={<Reactivation />} />
                <Route path="/raporlar" element={<Reports />} />
                <Route path="/personel" element={<Staff />} />
                <Route path="/laboratuvar" element={<Lab />} />
                <Route path="/stok" element={<Inventory />} />
                <Route path="/tedarikciler" element={<Suppliers />} />
                <Route path="/bildirimler" element={<Notifications />} />
                <Route path="/destek" element={<Support />} />
                <Route path="/gorevler" element={<Tasks />} />
                <Route path="/geri-aranacaklar" element={<Callbacks />} />
                <Route path="/notlar" element={<Notes />} />
                <Route path="/cop-kutusu" element={<RecycleBin />} />
                <Route path="/aktivite-kayitlari" element={<Audit />} />
                <Route path="/ayarlar" element={<Settings />} />
                <Route path="/abonelik" element={<Subscription />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
